import _ from "lodash";
import moment from "moment";
import { OrganisationsApi } from "../../../../../../services/api/v2/organisations/Organisations.api";
import { IFormFieldModel } from "../../../../../../core/forms/formField/IFormField";
import { IAutocompleteModel } from "../../../../../../core/forms/controls/autocomplete/IAutocompleteModel";
import { INIT_AUTOCOMPLETE } from "../../../../../../core/forms/controls/autocomplete/Autocomplete_init";
import { AutocompleteOption } from "../../../../../../components/ui/_forms/Autocomplete/AutocompleteOption";
import { IconSymbols } from "../../../../../../components/ui/Icon";
import { FORM_COL } from "../../../../../../constants";
import { generateFormFieldsFromJson } from "../../../../../../core/forms/helpers/FormFieldMappers";
import { INIT_TEXT_FIELD } from "../../../../../../core/forms/controls/textField/TextField_init";
import I18n from "../../../../../../core/localization/I18n";
import { IDatePickerModel } from "../../../../../../core/forms/controls/datePicker/IDatePickerModel";
import { INIT_DATEPICKER } from "../../../../../../core/forms/controls/datePicker/DatePicker_model";
import { FormRequiredFieldIndicator } from "../../../../../../components/ui/_forms/FormRequiredFieldIndicator/FormRequiredFieldIndicator";
import { Validations } from "../../../../../../core/forms/helpers/Validations";
import { ErrorMessage } from "../../../../../../components/ui/ErrorMessage";
import { ICheckboxModel } from "../../../../../../core/forms/controls/checkbox/ICheckboxModel";
import { INIT_CHECKBOX_FIELD } from "../../../../../../core/forms/controls/checkbox/Checkbox_init";
import { ITextFieldModel } from "../../../../../../core/forms/controls/textField/ITextFieldModel";
import { IOrganisationPermission } from "../../../../../../contexts/permissions/PermissionsTypes";

export const GetCopyProjectFormFields = (
  organisationId: number,
  orgProvider: OrganisationsApi,
  project?: FP.Entities.IProject,
  organisations?: Dictionary<IOrganisationPermission>
): IFormFieldModel<any, any>[] => {
  let actualEndDate = {
    ...INIT_TEXT_FIELD,
    key: "actualEndDate",
    inputType: "hidden",
    value: project?.actualEndDate
  };

  const organisationField: Partial<IAutocompleteModel> = {
    ...INIT_AUTOCOMPLETE,
    key: "targetOrganisationId",
    label: <label htmlFor={"targetOrganisationId"}>Assign organisation: </label>,
    placeholder: "Select organisation",
    componentProps: {
      className: "form-control"
    },
    optionElement: (
      <AutocompleteOption key={"e"} className={"autocomplete__chip"} label={(e: FP.Entities.IOrganisation) => e.name} />
    ),
    onFocus: async function () {
      let self: IAutocompleteModel = this;
      this.componentProps.disabled = false;

      const sortedOrganisations = _.orderBy(organisations, [org => org.name.toLowerCase()]);
      self.setOptions(sortedOrganisations);
    },
    extractValue: function () {
      return this.value?.id;
    },
    valueLabelFn: (obj: FP.Entities.IOrganisation) => obj.name,
    fieldClassName: `${FORM_COL.FULL_WIDTH}`,
    value: project?.organisation
  };

  const startDate: Partial<IDatePickerModel> = {
    ...INIT_DATEPICKER,
    componentProps: {
      ...INIT_DATEPICKER.componentProps,
      icon: IconSymbols.Calendar,
      showClearDate: true,
      datePickerProps: {
        ...INIT_DATEPICKER.componentProps.datePickerProps,
        isOutsideRange: day => {
          if (project?.actualEndDate) {
            return day >= moment(project.actualEndDate);
          }
        }
      }
    },
    onValueChange: (value, model) => {
      const endDateChannelValue = model.channels.initialEndDate.value;
      (model as any).subscribers
        .find(e => e.key === "initialEndDate")
        .setDatepickerProps({
          ...INIT_DATEPICKER.componentProps.datePickerProps,
          isOutsideRange: day => {
            return day >= moment(endDateChannelValue);
          },
          initialVisibleMonth: () => (endDateChannelValue != null ? endDateChannelValue : moment())
        });
    },
    subscribeTo: ["initialEndDate"],
    key: "startDate",
    label: (
      <label htmlFor="startDate">
        {I18n.t("forms.startDate")} <FormRequiredFieldIndicator />
      </label>
    ),
    placeholder: "dd/mm/yyyy",
    fieldClassName: FORM_COL.FULL_WIDTH,
    validate: function () {
      let self: IDatePickerModel = this;
      let res = true;
      if (Validations.IS_EMPTY(self.extractValue())) {
        self.errorMessage = <ErrorMessage>{I18n.t("validations.startDate")}</ErrorMessage>;
        res = false;
      }
      return res;
    },
    value: project && moment(project.startDate),
    extractValue: function () {
      return this.value;
    },
    onChannelFieldChanged: async function (field) {
      const self: IDatePickerModel = this;
      if (field.key === "initialEndDate") {
        const sd = field.value;
        self.setDatepickerProps({
          ...INIT_DATEPICKER.componentProps.datePickerProps,
          isOutsideRange: day => {
            return day >= moment(sd);
          },
          initialVisibleMonth: () => (sd != null ? sd : moment())
        });
      }
    }
  };
  const shouldCloneMilestones: Partial<ICheckboxModel> = {
    ...INIT_CHECKBOX_FIELD,
    key: "shouldCloneMilestones",
    label: I18n.t("forms.shouldCloneMilestones"),
    value: false,
    fieldClassName: `${FORM_COL.FULL_WIDTH} form-check d-flex align-items-center`
  };

  const shouldCloneAudiences: Partial<ICheckboxModel> = {
    ...INIT_CHECKBOX_FIELD,
    key: "shouldCloneAudiences",
    label: I18n.t("forms.shouldCloneAudiences"),
    value: false,
    fieldClassName: `${FORM_COL.FULL_WIDTH} form-check d-flex align-items-center`
  };
  const shouldCloneImpacts: Partial<ICheckboxModel> = {
    ...INIT_CHECKBOX_FIELD,
    key: "shouldCloneImpacts",
    label: I18n.t("forms.shouldCloneImpacts"),
    value: false,
    fieldClassName: `${FORM_COL.FULL_WIDTH} form-check d-flex align-items-center`
  };
  const shouldCloneImpactsGroup: Partial<ICheckboxModel> = {
    ...INIT_CHECKBOX_FIELD,
    key: "shouldCloneImpactGroups",
    label: I18n.t("forms.shouldCloneImpactGroups"),
    value: false,
    fieldClassName: `${FORM_COL.FULL_WIDTH} form-check d-flex align-items-center`
  };
  const shouldClonePhasesAndPulses: Partial<ICheckboxModel> = {
    ...INIT_CHECKBOX_FIELD,
    key: "shouldClonePhasesAndPulses",
    label: I18n.t("forms.shouldClonePhasesAndPulses"),
    value: false,
    fieldClassName: `${FORM_COL.FULL_WIDTH} form-check d-flex align-items-center`
  };
  const shouldCloneActions: Partial<ICheckboxModel> = {
    ...INIT_CHECKBOX_FIELD,
    key: "shouldCloneActions",
    label: I18n.t("forms.shouldCloneActions"),
    value: false,
    fieldClassName: `${FORM_COL.FULL_WIDTH} form-check d-flex align-items-center`
  };

  const fields = [];

  if (organisationId) {
    const orgId: Partial<ITextFieldModel> = {
      ...INIT_TEXT_FIELD,
      key: "organisationId",
      inputType: "hidden",
      value: organisationId + "",
      defaultValue: organisationId + ""
    };
    fields.push(orgId);
  }
  fields.push(organisationField);
  fields.push(startDate);
  fields.push(actualEndDate);
  fields.push(shouldCloneMilestones);
  fields.push(shouldCloneAudiences);
  fields.push(shouldCloneImpacts);
  fields.push(shouldCloneImpactsGroup);
  fields.push(shouldClonePhasesAndPulses);
  fields.push(shouldCloneActions);
  const models = generateFormFieldsFromJson(fields);
  return models;
};
